import { Component, Inject, Input, OnInit } from '@angular/core';
import { BaseCustomFieldViewComponent } from '../../../../../base/form';
import { BehaviorSubject, Subscription } from 'rxjs';
import { AssetActualFault } from '../../../types';
import { ViewService } from '../../../../../services/view.service';
import { blank, data_get, filled, observe, OptionalValue } from '../../../../../shared/utils/common';
import { DIALOG_DATA, DialogService } from '../../../../../services/dialog.service';
import { AssetService } from '../../../../../services/asset.service';
import { FieldmagicDropdownOption } from '../../../../../shared/components/forms/input/dropdown/dropdown.component';

@Component({
  selector: 'assets-view-actual-faults',
  template: `
    <div fieldmagicGridRow>
      <div fieldmagicGridColumn="12">
        <div class="d-flex flex-column d-flex-gap w-100">
          <div class="d-flex justify-content-end w-100">
            <fieldmagic-dropdown-input
              *ngIf="filterable"
              [(ngModel)]="filter"
              class="w-20"
              [standalone]="true"
              placeholder="Please select filter"
              [options]="filters$ | async"
              [clearable]="false"
              (ngModelChange)="onFilterChange()"
            >
            </fieldmagic-dropdown-input>
          </div>

          <table class="table table-bordered">
            <thead>
              <tr>
                <td
                  width="30%"
                >
                  <fieldmagic-text
                    content="Defect"
                    purpose="table-header"
                  >
                  </fieldmagic-text>
                </td>
                <td
                  width="26%"
                >
                  <fieldmagic-text
                    content="Comments"
                    purpose="table-header"
                  >
                  </fieldmagic-text>
                </td>
                <td
                  width="5%"
                >
                  <fieldmagic-text
                    content="Acknowledged?"
                    purpose="table-header"
                    alignment="center"
                  >
                  </fieldmagic-text>
                </td>
                <td
                  width="5%"
                >
                  <fieldmagic-text
                    content="Resolved?"
                    purpose="table-header"
                    alignment="center"
                  >
                  </fieldmagic-text>
                </td>
                <td
                  width="10%"
                >
                  <fieldmagic-text
                    content="Job"
                    purpose="table-header"
                    alignment="center"
                  >
                  </fieldmagic-text>
                </td>
                <td
                  width="10%"
                >
                  <fieldmagic-text
                    content="Quote"
                    purpose="table-header"
                    alignment="center"
                  >
                  </fieldmagic-text>
                </td>
                <td
                  width="16%"
                >
                  <fieldmagic-text
                    content="Date Reported"
                    purpose="table-header"
                  >
                  </fieldmagic-text>
                </td>
              </tr>
            </thead>

            <tbody>
              <ng-container *ngIf="fetching$ | async">
                <tr class="text-center">
                  <td colspan="3">
                    <fieldmagic-icon [spin]="true"></fieldmagic-icon>
                  </td>
                </tr>
              </ng-container>

              <ng-container *ngIf="! (fetching$ | async)">
                <ng-container *ngIf="faults$ | async as faults">
                  <tr
                    *ngIf="faults | blank"
                    class="text-center"
                  >
                    <td colspan="7">No actual defects recorded.</td>
                  </tr>

                  <ng-container *ngFor="let fault of faults; let fault_index = index">
                    <tr>
                      <td
                        [style.background]="fault.color"
                        [style.color]="fault.color | bg_text_color"
                      >
                        {{ '(%s) %s' | sprintf: [fault.severity, fault.fault] }}
                      </td>

                      <td>
                        {{ fault.comments | placeholdWithString }}
                      </td>

                      <td class="text-center">
                        <div class="d-flex d-flex-gap w-100 justify-content-center">
                          <span>{{ (fault.acknowledged ? 'yes' : 'no') | translate }}</span>

                          <fieldmagic-icon
                            *ngIf="fault.acknowledged"
                            [icon]="[{
                              icon: 'info-circle',
                              klass: 'text-info'
                            }]"
                            [tooltip]="'Acknowledged By: %s' | translate | sprintf: [fault.acknowledged_by_text]"
                          >
                          </fieldmagic-icon>
                        </div>
                      </td>

                      <td>
                        <div class="d-flex d-flex-gap w-100 justify-content-center">
                          <span>{{ (fault.resolved ? 'yes' : 'no') | translate }}</span>

                          <fieldmagic-icon
                            *ngIf="fault.resolved"
                            [icon]="[{
                              icon: 'info-circle',
                              klass: 'text-info'
                            }]"
                            tooltip="View resolution details"
                            purpose="clicking"
                            (click)="onViewResolutionDetails(fault)"
                          >
                          </fieldmagic-icon>
                        </div>
                      </td>

                      <td class="text-center">
                        <ng-container *ngIf="fault | data_get: 'job_text' | filled">
                          <a
                            [routerLink]="['/jobs', fault.job_id]"
                            target="_blank"
                          >{{ '#%s' | sprintf: [fault.job_text] }}</a>
                        </ng-container>

                        <ng-container *ngIf="fault | data_get: 'job_text' | blank">
                          --
                        </ng-container>
                      </td>

                      <td class="text-center">
                        <ng-container *ngIf="fault | data_get: 'opportunity_text' | filled">
                          <a
                            [routerLink]="['/opportunities', fault.opportunity_id]"
                            target="_blank"
                          >{{ '#%s' | sprintf: [fault.opportunity_text] }}</a>
                        </ng-container>

                        <ng-container *ngIf="fault | data_get: 'opportunity_text' | blank">
                          <span>--</span>
                        </ng-container>
                      </td>

                      <td>
                        <div class="d-flex d-flex-gap align-items-center w-100">
                          <span>{{ fault.date_reported | format: 'll' }}</span>

                          <fieldmagic-icon
                            [icon]="[{
                              icon: 'info-circle',
                              klass: 'text-info'
                            }]"
                            tooltip="View report details"
                            purpose="clicking"
                            (click)="onViewReportDetails(fault)"
                          >
                          </fieldmagic-icon>

                          <a
                            *ngIf="fault.image_path | filled"
                            href="{{ fault.image_url }}"
                            target="_blank"
                            matTooltip="{{ 'View Photo' | translate }}"
                            class="btn btn-link"
                          >
                            <fieldmagic-icon
                              icon="file-image"
                            >
                            </fieldmagic-icon>
                          </a>
                        </div>
                      </td>
                    </tr>
                  </ng-container>
                </ng-container>
              </ng-container>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  `,
  providers: [
    DialogService,
  ],
})
export class AssetViewActualFaultsComponent extends BaseCustomFieldViewComponent {
  filter?: string = 'unresolved';

  @Input() faults: AssetActualFault[] = [];

  @Input() filterable: boolean = true;

  readonly fetching$ = new BehaviorSubject<boolean>(true);

  readonly faults$ = new BehaviorSubject<AssetActualFault[]>([]);

  readonly filters$ = new BehaviorSubject<FieldmagicDropdownOption<string>[]>([
    'unresolved',
    'pending_review',
    'resolved',
  ]);

  private _onFetchingFaults?: Subscription;

  constructor(
    private readonly _view: ViewService,
    private readonly _dialog: DialogService,
    private readonly _assets: AssetService,
  ) {
    super();
  }

  ngOnInit(): void {
    if (blank(data_get(this._view.getViewRecord(), 'id'))
      && blank(this.faults)
    ) {
      this.fetching$.next(false);
    } else if (filled(this.faults)) {
      this.fetching$.next(false);
      this.faults$.next(this.faults);
    } else {
      this._fetchFaults();
    }
  }

  ngOnDestroy(): void {
    if (this._onFetchingFaults) {
      this._onFetchingFaults.unsubscribe();
    }
  }

  onViewResolutionDetails = (fault: AssetActualFault) => this._dialog.show({
    component: AssetFaultResolutionDetailsDialogComponent,
    data: {
      fault,
    },
  });

  onViewReportDetails = (fault: AssetActualFault) => this._dialog.show({
    component: AssetFaultReportDetailsDialogComponent,
    data: {
      fault,
    },
  });

  onFilterChange = () => this._fetchFaults();

  private _fetchFaults() {
    this._onFetchingFaults = observe({
      before: () => this.fetching$.next(true),
      after: () => this.fetching$.next(false),
      observable: () => this._assets.getFaults({
        id: data_get(this._view.getViewRecord(), 'id'),
        sort_by_criticality: true,
        filter: this.filter,
      }),
    })
    .subscribe((faults) => this.faults$.next(faults));
  }
}

@Component({
  selector: 'asset-fault-resolution-details-dialog',
  template: `
    <fieldmagic-dialog
      label="Resolution Details"
      icon="file-alt"
      [instance]="this"
    >
      <ng-template fieldmagicDialogButtonsTmp>
      </ng-template>

      <ng-template fieldmagicDialogContentTmp>
        <table class="table table-bordered">
          <thead>
            <tr>
              <td
                width="30%"
              >
                <fieldmagic-text
                  content="Resolution Notes"
                  purpose="table-header"
                >
                </fieldmagic-text>
              </td>

              <td
                width="30%"
              >
                <fieldmagic-text
                  content="Resolved By"
                  purpose="table-header"
                >
                </fieldmagic-text>
              </td>

              <td
                width="30%"
              >
                <fieldmagic-text
                  content="Date Resolved"
                  purpose="table-header"
                >
                </fieldmagic-text>
              </td>
            </tr>
          </thead>

          <tbody *ngIf="fault$ | async as fault">
            <td>
              {{ fault.resolution_notes | placeholdWithString }}
            </td>

            <td>
              {{ fault.resolved_by_text }}
            </td>

            <td>
              {{ fault.date_resolved | format: 'll' }}
            </td>
          </tbody>
        </table>
      </ng-template>
    </fieldmagic-dialog>
  `
})
export class AssetFaultResolutionDetailsDialogComponent implements OnInit {
  readonly fault$ = new BehaviorSubject<OptionalValue<AssetActualFault>>(null);

  constructor(
    @Inject(DIALOG_DATA) private readonly _data: AssetFaultResolutionDetailsDialogData,
  ) {}

  ngOnInit(): void {
    this.fault$.next(this._data.fault);
  }
}

type AssetFaultResolutionDetailsDialogData = {
  fault: AssetActualFault;
}

@Component({
  selector: 'asset-fault-report-details-dialog',
  template: `
    <fieldmagic-dialog
      label="Report Details"
      icon="file-alt"
      [instance]="this"
    >
      <ng-template fieldmagicDialogButtonsTmp>
      </ng-template>

      <ng-template fieldmagicDialogContentTmp>
        <table class="table table-bordered">
          <thead>
            <tr>
              <td
                width="30%"
              >
                <fieldmagic-text
                  content="Reported By"
                  purpose="table-header"
                >
                </fieldmagic-text>
              </td>

              <td
                width="30%"
              >
                <fieldmagic-text
                  content="Job Number"
                  purpose="table-header"
                >
                </fieldmagic-text>
              </td>

              <td
                width="30%"
              >
                <fieldmagic-text
                  content="Date Reported"
                  purpose="table-header"
                >
                </fieldmagic-text>
              </td>

              <td
                width="10%"
              >
                <fieldmagic-text
                  content="Photo"
                  purpose="table-header"
                  alignment="center"
                >
                </fieldmagic-text>
              </td>

            </tr>
          </thead>

          <tbody *ngIf="fault$ | async as fault">
            <td>
              {{ fault.reported_by_text }}
            </td>

            <td>
              <a
                *ngIf="fault.job_id | filled"
                [routerLink]="['jobs', fault.job_id]"
                target="_blank"
              >
                {{ '#%s' | sprintf: [fault.job_text] }}
              </a>

              <span *ngIf="fault.job_id | blank">--</span>
            </td>

            <td>
              {{ fault.date_reported | format: 'll' }}
            </td>

            <td class="text-center">
              <a
                *ngIf="fault.image_url | filled"
                href="{{ fault.image_url }}"
                target="_blank"
              >
                {{ 'View Photo' | translate }}
              </a>

              <span *ngIf="fault.image_url | blank">--</span>
            </td>
          </tbody>
        </table>
      </ng-template>
    </fieldmagic-dialog>
  `
})
export class AssetFaultReportDetailsDialogComponent implements OnInit {
  readonly fault$ = new BehaviorSubject<OptionalValue<AssetActualFault>>(null);

  constructor(
    @Inject(DIALOG_DATA) private readonly _data: AssetFaultReportDetailsDialogData,
  ) {}

  ngOnInit(): void {
    this.fault$.next(this._data.fault);
  }
}

type AssetFaultReportDetailsDialogData = {
  fault: AssetActualFault;
}